import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Image,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import firebase from 'firebase/app';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useConfig } from '../../../hooks/useConfig';
import { uploadImage } from '../../../utils/uploadImage';

interface FormData {
  image?: File[];
}

interface AdminConfigStandardImageModalProps {
  firebaseRef: firebase.firestore.DocumentReference;
  previewImageRef: string;
  configKey: string;
  storageKey: string;
  title: string;
  description: string;
  cta: string;
}

export const AdminConfigStandardImageModal: React.FC<AdminConfigStandardImageModalProps> =
  ({
    previewImageRef,
    firebaseRef,
    configKey,
    title,
    description,
    cta,
    storageKey,
  }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isLoading, setIsLoading] = useState(false);
    const [imageSrc, setImageSrc] = useState('');

    const config = useConfig();
    const { register, formState, handleSubmit } = useForm<FormData>();
    const { errors } = formState;
    const toast = useToast();

    useEffect(() => {
      config &&
        firebase
          .storage()
          .ref(previewImageRef)
          .getDownloadURL()
          .then((url) => setImageSrc(url));

      return () => {};
    }, [config]);

    const onSubmit = handleSubmit(async ({ image }) => {
      const file = image && image[0];
      if (file && config) {
        try {
          setIsLoading(true);
          const url = await uploadImage(file, storageKey, 'config');
          await firebaseRef.update({
            [configKey]: { cover: url.fullPath, coverThumb: url.x700 },
          });
          setIsLoading(false);
          toast({
            title: 'Bild erfolgreich hochgeladen',
            duration: 9000,
            isClosable: true,
            status: 'success',
          });
        } catch (e) {
          setIsLoading(false);
          toast({
            title: 'Fehler',
            duration: 9000,
            isClosable: true,
            status: 'error',
          });
          // eslint-disable-next-line no-console
          console.log(e);
        }
      }
      onClose();
    });

    return (
      <>
        <Button isFullWidth={true} borderRadius="xl" onClick={onOpen}>
          {cta}
        </Button>
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent p={6} borderRadius="xl" boxShadow="xl">
            <form onSubmit={onSubmit}>
              <Heading size="md" mb={4}>
                {title}
              </Heading>
              <ModalCloseButton />

              {config && <Image src={imageSrc} w="100%" h="auto" />}
              <Text mt={2}>{description}</Text>
              <FormControl isInvalid={!!errors.image} my={6}>
                <FormLabel>Bild hochladen</FormLabel>
                <Input
                  focusBorderColor="brand.500"
                  type="file"
                  py={10}
                  textAlign="center"
                  style={{ display: 'flex', justifyContent: 'center' }}
                  accept="image/gif, image/jpeg, image/png"
                  placeholder="Bild auswählen"
                  {...register('image', {
                    validate: (file) =>
                      (file &&
                        (!file[0] || (file[0] && file[0].size < 5242880))) ||
                      false,
                    required: false,
                  })}
                />
                <FormErrorMessage>
                  {errors.image &&
                    'Bitte lade nur Bilder hoch, die kleiner als 5mb sind.'}
                </FormErrorMessage>
              </FormControl>
              <Flex
                justify="flex-end"
                direction={['column', 'column', 'row']}
                mt={4}
              >
                <Button variant="ghost" mr={3} onClick={onClose}>
                  Schließen
                </Button>
                <Button isLoading={isLoading} colorScheme="brand" type="submit">
                  Speichern
                </Button>
              </Flex>
            </form>
          </ModalContent>
        </Modal>
      </>
    );
  };
